import { useMemo } from 'react';
import { create } from 'zustand';
import i18n from '../i18n';
import { authSwitchedError, isAuthSwitchedElsewhere } from './authTabSync';
import { toast, useToastStore } from '../store/toastStore';
import { announce } from '../components/ui/LiveAnnouncer';
import { apiErrorMessage } from '../components/sales/shared';
import { createDeleteScheduler, makeKeepaliveSender, offerStatusUndo, type DeleteJob } from './undoableActionsCore';
import { UNDO_WINDOW_MS } from './undoableCommit';

/**
 * P-27 (D-166 s.11) - the one place that gives a delete and a status change a
 * "בטל" for 6 seconds, through the existing toast (`toastStore`, tokens only).
 * See `undoableActionsCore.ts` for the behaviour.
 */

// -- rows hidden while their DELETE waits ------------------------------------
interface HiddenState {
  hidden: Record<string, true>;
  setHidden: (kind: string, id: string, on: boolean) => void;
}
export const useHiddenRows = create<HiddenState>((set) => ({
  hidden: {},
  setHidden: (kind, id, on) => set((s) => {
    const key = `${kind}:${id}`;
    const next = { ...s.hidden };
    if (on) next[key] = true; else delete next[key];
    return { hidden: next };
  }),
}));

/** Rows of `kind` to leave out of a list / table right now. */
export function useHiddenIds(kind: string): (id: string) => boolean {
  const hidden = useHiddenRows((s) => s.hidden);
  return (id) => hidden[`${kind}:${id}`] === true;
}

/** Drop the hidden rows from a list (`kind` as given to `undoableDelete`). Stable while nothing is hidden. */
export function useWithoutHidden<T extends { id: string }>(kind: string, rows: T[]): T[] {
  const hidden = useHiddenRows((s) => s.hidden);
  return useMemo(() => {
    const prefix = `${kind}:`;
    if (!Object.keys(hidden).some((k) => k.startsWith(prefix))) return rows;
    return rows.filter((r) => hidden[`${prefix}${r.id}`] !== true);
  }, [kind, rows, hidden]);
}

// -- wiring ------------------------------------------------------------------
const show = (message: string, onUndo: () => void): string =>
  toast('success', message, {
    duration: UNDO_WINDOW_MS,
    action: { label: i18n.t('common.undo'), onClick: onUndo },
  });
const dismiss = (toastId: string) => useToastStore.getState().removeToast(toastId);
const undone = () => announce(i18n.t('shell.undone'));

const scheduler = createDeleteScheduler({
  show,
  dismiss,
  setHidden: (kind, id, on) => useHiddenRows.getState().setHidden(kind, id, on),
  undone,
  failed: (error) => toast('error', apiErrorMessage(error, i18n.t('undoToast.failed'))),
  whenVisible: (fn) => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') { fn(); return; }
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      document.removeEventListener('visibilitychange', onVisible);
      fn();
    };
    document.addEventListener('visibilitychange', onVisible);
  },
});

// Closing the tab / leaving the app sends every waiting delete at once: never lost.
// A plain XHR is cancelled when the tab closes, so the request goes out as `fetch` with
// `keepalive` (same base URL and Bearer token as the api client). Also on visibility hidden.
const API_BASE = import.meta.env.VITE_API_URL || '/api';
export const sendKeepalive = makeKeepaliveSender( // DQ-115: also used by useUndoableMutation
  // D-279 (QX-ב): a tab whose user was replaced in another tab sends nothing.
  (url, init) => (isAuthSwitchedElsewhere()
    ? Promise.reject(authSwitchedError(i18n.t('authTabSync.blocked')))
    : fetch(url, init as RequestInit)),
  API_BASE,
  () => { try { return localStorage.getItem('auth_token'); } catch { return null; } },
);
if (typeof window !== 'undefined') {
  const flush = () => { void scheduler.flushOnHide(sendKeepalive); };
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(); });
}

/**
 * Hard delete with "בטל". The row is hidden at once (`useWithoutHidden(kind, rows)`),
 * the DELETE leaves after 6 s, "בטל" cancels it. The scheduler lives outside any
 * component, so leaving the page (e.g. after deleting from a detail page) does
 * not send it early.
 */
export function undoableDelete(job: DeleteJob) {
  return scheduler.schedule(job);
}

/** Send every waiting delete now (tests / explicit flush). */
export const flushPendingDeletes = () => scheduler.flushAll();

/**
 * After a status change that already happened: "בטל" sends `revert()` (the
 * previous status, through the same route). Call it from the mutation's onSuccess.
 */
export function offerUndoStatus(opts: {
  message: string;
  revert: () => Promise<unknown>;
  onReverted?: () => void;
}) {
  offerStatusUndo({ show, dismiss, undone }, {
    message: opts.message,
    revert: opts.revert,
    onReverted: opts.onReverted,
    onRevertFailed: (err) => toast('error', apiErrorMessage(err, i18n.t('undoToast.revertFailed'))),
  });
}

/** D-303: an undo that re-enters "הסתיים" of a project / development with no warranty is refused by the server (400):
 *  the screen then opens the warranty window instead of showing an error. */
export function isWarrantyRefusal(err: unknown): boolean {
  return (err as { response?: { status?: number } } | null)?.response?.status === 400;
}

/** True when a status change may be undone with a plain revert (no one-way automation). */
export function statusIsReversible(from: string | null | undefined, to: string, oneWay: readonly string[]): boolean {
  return !!from && from !== to && !oneWay.includes(from) && !oneWay.includes(to);
}
